import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Trophy, ArrowRight, Zap } from "lucide-react";
import { Section, SectionTitle, TeamLogo, Reveal } from "../components/UIBits";
import { TOURNAMENT, phaseLabel } from "../lib/constants";
import { categoryMatches } from "../lib/categories";
import { useT } from "../lib/i18n";
import api from "../lib/api";

/** Groups the ordered phase columns we render on desktop. */
const PHASE_COLUMNS = ["quart", "demi", "finale"];

/** Extract a compact source label ("#1", "G-501") from a home_source/away_source string. */
function shortSource(src) {
    if (!src) return null;
    const s = String(src).trim().toLowerCase();
    const m = s.match(/^(seed|winner|loser):(\d+)$/);
    if (!m) return null;
    const [, kind, val] = m;
    if (kind === "seed") return `#${val}`;
    if (kind === "winner") return `G-${val}`;
    if (kind === "loser") return `P-${val}`;
    return null;
}

/** BracketSlot: renders one side of a match card (team logo + name + score). */
function BracketSlot({ side, game, teamsById, isWinner, tbdLabel }) {
    const tid = game[`${side}_team_id`];
    const placeholder = game[`${side}_placeholder`] || "";
    const source = game[`${side}_source`] || "";
    const team = tid ? teamsById[tid] : null;
    const score = game[`${side}_score`];
    const showScore = game.status === "final" || game.status === "in_progress";

    return (
        <div
            className={`flex items-center justify-between gap-3 px-3 py-2.5 transition-all ${
                isWinner ? "bg-[#7FD93C]/10" : ""
            }`}
            data-testid={`slot-${game.game_num}-${side}`}
        >
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
                {team ? (
                    <TeamLogo team={team} size={26} />
                ) : (
                    <div className="w-[26px] h-[26px] rounded-sm border border-dashed border-white/20 shrink-0" />
                )}
                <div className="min-w-0">
                    <div
                        className={`truncate font-semibold text-sm ${
                            isWinner ? "text-[#7FD93C]" : team ? "text-white" : "text-white/40"
                        }`}
                    >
                        {team ? team.name : placeholder || tbdLabel}
                    </div>
                    {!team && shortSource(source) && (
                        <div className="text-[10px] font-mono text-[#4FE0D5]/60 tracking-wider">
                            {shortSource(source)}
                        </div>
                    )}
                </div>
            </div>
            <div
                className={`font-display text-2xl leading-none tabular-nums shrink-0 ${
                    showScore
                        ? isWinner
                            ? "text-[#7FD93C]"
                            : "text-white/80"
                        : "text-white/20"
                }`}
            >
                {showScore ? score ?? 0 : "—"}
            </div>
        </div>
    );
}

/** BracketMatch: one full match card. */
function BracketMatch({ game, teamsById, tbdLabel, liveLabel, finalLabel, gameLabel }) {
    const hs = game.home_score ?? 0;
    const as_ = game.away_score ?? 0;
    const isFinal = game.status === "final";
    const isLive = game.status === "in_progress";
    const homeWins = isFinal && hs > as_;
    const awayWins = isFinal && as_ > hs;

    return (
        <Link
            to={`/match/${game.game_num}`}
            className="block glass-card overflow-hidden border border-white/10 hover:border-[#7FD93C]/60 hover:-translate-y-0.5 transition-all"
            data-testid={`bracket-match-${game.game_num}`}
        >
            {/* Header strip */}
            <div className="flex items-center justify-between px-3 py-1.5 bg-black/40 border-b border-white/5">
                <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] tracking-widest text-white/40 uppercase">
                        {gameLabel} #{game.game_num}
                    </span>
                    {game.day && (
                        <span className="text-[10px] text-white/30 hidden sm:inline">
                            · {game.day}
                            {game.time ? ` ${game.time}` : ""}
                        </span>
                    )}
                </div>
                {isLive ? (
                    <span className="flex items-center gap-1 text-[10px] font-display tracking-widest text-[#7FD93C]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#7FD93C] animate-pulse" />
                        {liveLabel}
                    </span>
                ) : isFinal ? (
                    <span className="text-[10px] font-display tracking-widest text-[#4FE0D5]">
                        {finalLabel}
                    </span>
                ) : null}
            </div>
            <BracketSlot
                side="home"
                game={game}
                teamsById={teamsById}
                isWinner={homeWins}
                tbdLabel={tbdLabel}
            />
            <div className="h-px bg-white/5" />
            <BracketSlot
                side="away"
                game={game}
                teamsById={teamsById}
                isWinner={awayWins}
                tbdLabel={tbdLabel}
            />
        </Link>
    );
}

export default function PlayoffsPage() {
    const { t, lang } = useT();
    const [cat, setCat] = useState(TOURNAMENT.categories[0]);
    const [games, setGames] = useState(null);
    const [teams, setTeams] = useState([]);

    useEffect(() => {
        let active = true;
        const load = () =>
            Promise.all([api.get("/games"), api.get("/teams?light=true")]).then(
                ([g, tRes]) => {
                    if (!active) return;
                    setGames(g.data || []);
                    setTeams(tRes.data || []);
                }
            );
        load();
        const i = setInterval(load, 20000);
        return () => {
            active = false;
            clearInterval(i);
        };
    }, []);

    const teamsById = useMemo(() => {
        const m = {};
        (teams || []).forEach((tm) => {
            m[tm.id] = tm;
        });
        return m;
    }, [teams]);

    /** Games for the selected category, filtered to playoff phases and sorted. */
    const catGames = useMemo(() => {
        if (!games) return [];
        return games.filter((g) => categoryMatches(cat, g.category));
    }, [games, cat]);

    const columns = useMemo(() => {
        const map = { quart: [], demi: [], finale: [] };
        catGames.forEach((g) => {
            const p = g.phase;
            if (p === "quart") map.quart.push(g);
            else if (p === "demi") map.demi.push(g);
            else if (p === "finale" || p === "finale_d2" || p === "finale_d3") {
                map.finale.push(g);
            }
        });
        Object.keys(map).forEach((k) => {
            map[k].sort((a, b) => (a.game_num || 0) - (b.game_num || 0));
        });
        return map;
    }, [catGames]);

    const hasPlayoffGames =
        columns.quart.length + columns.demi.length + columns.finale.length > 0;

    // Extract the champion (winner of the "finale" game, if final).
    const champion = useMemo(() => {
        const f = columns.finale.find((g) => g.phase === "finale" && g.status === "final");
        if (!f) return null;
        const hs = f.home_score ?? 0;
        const as_ = f.away_score ?? 0;
        if (hs === as_) return null;
        const tid = hs > as_ ? f.home_team_id : f.away_team_id;
        return teamsById[tid] || null;
    }, [columns.finale, teamsById]);

    const tbdLabel = t("playoffs.tbd");
    const liveLabel = t("playoffs.live_now");
    const finalLabel = t("playoffs.final_score");
    const gameLabel = t("playoffs.game_num");

    return (
        <div data-testid="playoffs-page">
            <Helmet>
                <title>{`${t("playoffs.title_pre")} ${t("playoffs.title_accent")} — ${TOURNAMENT.name}`}</title>
            </Helmet>
            <Section>
                <SectionTitle
                    kicker={t("playoffs.kicker")}
                    title={t("playoffs.title_pre")}
                    accent={t("playoffs.title_accent")}
                />

                {/* Category tabs */}
                <div className="flex flex-wrap gap-2 mb-8">
                    {TOURNAMENT.categories.map((c) => (
                        <button
                            key={c}
                            data-testid={`playoffs-tab-${c}`}
                            onClick={() => setCat(c)}
                            className={`px-5 py-2 font-display text-lg tracking-widest uppercase angle-tag transition-all ${
                                cat === c
                                    ? "bg-[#7FD93C] text-black"
                                    : "bg-white/5 text-white/70 hover:bg-white/10"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>

                {/* Champion banner */}
                {champion && (
                    <Reveal className="mb-10">
                        <div
                            className="glass-card p-6 border-l-4 border-[#7FD93C] flex items-center gap-5"
                            data-testid="playoffs-champion-banner"
                        >
                            <Trophy size={44} className="text-[#7FD93C] shrink-0" />
                            <div className="flex-1 min-w-0 flex items-center gap-4">
                                <TeamLogo team={champion} size={56} />
                                <div className="min-w-0">
                                    <div className="text-[11px] uppercase tracking-[0.3em] text-[#4FE0D5]">
                                        {t("playoffs.winner")}
                                    </div>
                                    <div className="font-display text-3xl text-[#7FD93C] leading-tight truncate">
                                        {champion.name}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                )}

                {games === null ? (
                    <div className="text-white/50">{t("common.loading")}</div>
                ) : !hasPlayoffGames ? (
                    <div className="glass-card p-8 text-center text-white/60" data-testid="playoffs-empty">
                        {t("playoffs.no_games")}
                    </div>
                ) : (
                    <>
                        {/* Desktop bracket: horizontal columns with connector arrows */}
                        <div
                            className="hidden lg:grid gap-6 items-stretch"
                            style={{ gridTemplateColumns: "1fr auto 1fr auto 1fr" }}
                            data-testid="playoffs-desktop-bracket"
                        >
                            {PHASE_COLUMNS.map((phaseKey, idx) => (
                                <React.Fragment key={phaseKey}>
                                    <div className="flex flex-col justify-around gap-6">
                                        <div className="text-center mb-2">
                                            <div className="text-[11px] uppercase tracking-[0.3em] text-[#4FE0D5]">
                                                {t(`playoffs.phase.${phaseKey}`)}
                                            </div>
                                            <div className="mt-1 h-px bg-gradient-to-r from-transparent via-[#4FE0D5]/40 to-transparent" />
                                        </div>
                                        {columns[phaseKey].length === 0 ? (
                                            <div className="glass-card p-6 text-center text-white/30 text-sm italic border-dashed">
                                                {tbdLabel}
                                            </div>
                                        ) : (
                                            columns[phaseKey].map((g) => (
                                                <BracketMatch
                                                    key={g.game_num}
                                                    game={g}
                                                    teamsById={teamsById}
                                                    tbdLabel={tbdLabel}
                                                    liveLabel={liveLabel}
                                                    finalLabel={finalLabel}
                                                    gameLabel={gameLabel}
                                                />
                                            ))
                                        )}
                                    </div>
                                    {idx < PHASE_COLUMNS.length - 1 && (
                                        <div className="flex items-center justify-center">
                                            <ArrowRight
                                                size={22}
                                                className="text-[#4FE0D5]/50"
                                                strokeWidth={1.5}
                                            />
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>

                        {/* Mobile/tablet: stacked phases */}
                        <div className="lg:hidden grid gap-8" data-testid="playoffs-mobile-bracket">
                            {PHASE_COLUMNS.map((phaseKey) => (
                                <div key={phaseKey}>
                                    <div className="flex items-center gap-3 mb-3">
                                        <Zap size={16} className="text-[#7FD93C]" />
                                        <div className="font-display text-lg tracking-widest uppercase text-[#4FE0D5]">
                                            {t(`playoffs.phase.${phaseKey}`)}
                                        </div>
                                        <div className="flex-1 h-px bg-gradient-to-r from-[#4FE0D5]/40 to-transparent" />
                                    </div>
                                    {columns[phaseKey].length === 0 ? (
                                        <div className="glass-card p-5 text-center text-white/30 text-sm italic border-dashed">
                                            {tbdLabel}
                                        </div>
                                    ) : (
                                        <div className="grid gap-3">
                                            {columns[phaseKey].map((g) => (
                                                <BracketMatch
                                                    key={g.game_num}
                                                    game={g}
                                                    teamsById={teamsById}
                                                    tbdLabel={tbdLabel}
                                                    liveLabel={liveLabel}
                                                    finalLabel={finalLabel}
                                                    gameLabel={gameLabel}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* D2/D3 secondary finals (rendered separately, if present) */}
                        {(columns.finale.some((g) => g.phase === "finale_d2") ||
                            columns.finale.some((g) => g.phase === "finale_d3")) && (
                            <div className="mt-12">
                                <div className="text-[11px] uppercase tracking-[0.3em] text-white/40 mb-3">
                                    {lang === "en" ? "Consolation finals" : "Finales de consolation"}
                                </div>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    {columns.finale
                                        .filter((g) => g.phase === "finale_d2" || g.phase === "finale_d3")
                                        .map((g) => (
                                            <div key={g.game_num}>
                                                <div className="text-xs uppercase tracking-widest text-[#4FE0D5] mb-2">
                                                    {phaseLabel(g.phase, lang)}
                                                </div>
                                                <BracketMatch
                                                    game={g}
                                                    teamsById={teamsById}
                                                    tbdLabel={tbdLabel}
                                                    liveLabel={liveLabel}
                                                    finalLabel={finalLabel}
                                                    gameLabel={gameLabel}
                                                />
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Section>
        </div>
    );
}
